import { useCallback, useEffect, useState } from 'react';

/**
 * Standardized browser History API screen manager.
 *
 * Lets the system Back/Forward buttons navigate between internal screens and
 * component states of a single page WITHOUT changing the base URL.
 *
 * It is a drop-in replacement for
 *   `const [screenNumber, setScreenNumber] = useState(0)`
 * that additionally records every screen change as a `history` entry, and
 * restores the matching screen when the user presses Back/Forward.
 *
 * Each hook instance stamps its own `instanceId` into the history state so a
 * page never reacts to history entries created by a different page/component.
 *
 * Usage:
 *   const { screenNumber, setScreenNumber, historyState, updateHistoryState } =
 *     useScreenHistory({ initialScreen: 0, initialState: { brandLine: 'parliament' } });
 *
 *   // forward navigation (adds a history entry):
 *   setScreenNumber(3);
 *   // "back" navigation initiated from the UI (replaces the current entry):
 *   setScreenNumber(3, true);
 *   // pass extra data alongside a screen change:
 *   setScreenNumber(5, false, { brandLine: 'marlboro' });
 *   // update nested/inner state without changing the top-level screen number:
 *   updateHistoryState({ selectedPackTitle: 'Parliament Silver' });
 *
 * @param {Object}  [options]
 * @param {number}  [options.initialScreen=0] Starting screen number.
 * @param {Object}  [options.initialState={}] Extra data kept in the base history entry.
 * @param {boolean} [options.enabled=true]    When false the hook behaves like a
 *                                             plain `useState` (no history writes).
 * @returns {{
 *   screenNumber: number,
 *   setScreenNumber: (number: number, isBack?: boolean, extraState?: Object) => void,
 *   historyState: Object,
 *   updateHistoryState: (extraState: Object) => void
 * }}
 */
const useScreenHistory = ({
  initialScreen = 0,
  initialState = {},
  enabled = true
} = {}) => {
  const [instanceId] = useState(() => Math.random().toString(36).substring(7));
  const [screenNumber, setScreenNumberState] = useState(initialScreen);
  const [historyState, setHistoryState] = useState(initialState);

  useEffect(() => {
    if (!enabled) return undefined;

    const handlePopState = (event) => {
      const state = event.state;
      if (
        state &&
        state.instanceId === instanceId &&
        typeof state.screenNumber === 'number'
      ) {
        setScreenNumberState(state.screenNumber);
        setHistoryState(state);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Signal the app-level popstate guard (App.js) that this page manages its
    // own history, so it does not "trap" the Back button on the root screen.
    window.__screenHistoryActive = true;

    const baseState = {
      ...initialState,
      screenNumber: initialScreen,
      instanceId
    };
    window.history.replaceState(baseState, '', window.location.href);
    setHistoryState(baseState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.__screenHistoryActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, instanceId]);

  const setScreenNumber = useCallback(
    (number, isBack = false, extraState = {}) => {
      setScreenNumberState(number);

      if (!enabled) return;

      const newState = {
        ...window.history.state,
        screenNumber: number,
        instanceId,
        ...extraState
      };
      setHistoryState(newState);

      if (isBack) {
        window.history.replaceState(newState, '', window.location.href);
      } else {
        window.history.pushState(newState, '', window.location.href);
      }
    },
    [enabled, instanceId]
  );

  const updateHistoryState = useCallback(
    (extraState) => {
      if (!enabled) return;

      const newState = {
        ...window.history.state,
        instanceId,
        ...extraState
      };
      setHistoryState(newState);
      window.history.pushState(newState, '', window.location.href);
    },
    [enabled, instanceId]
  );

  return { screenNumber, setScreenNumber, historyState, updateHistoryState };
};

export default useScreenHistory;
